import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNativeApp, resolveAppUrlPath } from '../utils/appLinks';
import { logger } from '../utils/logger';

export default function NativeAppUrlListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = useRef('');
  const handledLaunchRef = useRef(false);

  useEffect(() => {
    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    let sub: PluginListenerHandle | null = null;

    const handleUrl = (url?: string | null, source: 'launch' | 'open' = 'open') => {
      if (!url || cancelled) return;
      if (source === 'launch') {
        if (handledLaunchRef.current) return;
        handledLaunchRef.current = true;
      }
      const path = resolveAppUrlPath(url);
      if (!path) return;
      if (currentPathRef.current === path) return;
      currentPathRef.current = path;
      navigate(path, { replace: true });
    };

    const setupListeners = async () => {
      try {
        sub = await CapApp.addListener('appUrlOpen', ({ url }) => handleUrl(url));
      } catch (err) {
        logger.warn?.('DEEPLINK_LISTENER_FAILED', err);
      }
    };

    void setupListeners();
    void CapApp.getLaunchUrl()
      .then((res) => handleUrl(res?.url, 'launch'))
      .catch((err) => logger.warn?.('DEEPLINK_LAUNCH_URL_FAILED', err));

    return () => {
      cancelled = true;
      if (sub) {
        void sub.remove();
      }
    };
  }, [navigate]);

  return null;
}
